import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  DataTypes,
  ExternalLinkInfo,
  InputInfo,
  ModelDefinitionDto,
  ModelReview,
  ModelTypes,
  OutcomeInfo,
} from '../../models/APIModels';
import ReactQuill from 'react-quill';
import { getInputs } from '../../Utils/getFormInputs';
import { FormSection, InputProps } from '../../models/type';
import { camelCase, cloneDeep } from 'lodash';
import Form from '../Form/Form';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
} from '@mui/material';
import { ApiHelper } from '../../common/ApiHelper';
import Loader from '../Loader';
import { useAuth0 } from '@auth0/auth0-react';
import useScreenWidth from '../../hooks/useScreenWidth';
import useAppUtils from '../../Utils/useAppUtils';
import { useNavigate } from 'react-router-dom';
import './ModelViewer.css';
import { config } from '../../config';
import { useSelector } from 'react-redux';
import { userInfoSelector } from '../../store/slices/userInfo';
import { ExternalLinkType } from '../../enums';
import favicon from '../../../public/images/favicon.ico';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ItemContent from '../ItemContent';
import { ReviewModal } from '../Modals';
import OfficialLink from './OfficialLink';
import VideosAndImagesSection from './VideosAndImagesSection';
import ModelViewerLine from './ModelViewerLine';
import HeaderSection from './HeaderSection';
import { SearchSelector, UpdateSearchForm } from '../../store/slices/search';
import { useAppDispatch } from '../../store';
import { toast } from 'react-toastify';
import ClaimModel from '../Modals/ClaimModel';
import MuiButton from '@mui/material/Button';
import DetailsSection from './DetailsSection';
import Inference from './Inference';
import Progress from '../Loaders/Progress';

interface IProps {
  model: ModelDefinitionDto;
}
const defaultReview = {
  id: '',
  reviewTitle: '',
  reviewText: '',
  starRating: 0,
};

const ModelViewer = ({ model }: IProps) => {

  
  // ▶️ Variables .....



  const profilePageUrl = `${window.location.origin}/profile/${model.author}`;
  const [isHostedModel, setIsHostedModel] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const [openTooltip, setOpenTooltip] = useState(false);
  const widthSize = useScreenWidth();
  const { alert, confirm } = useAppUtils();
  const [reviews, setReviews] = useState<ModelReview[]>([]);
  const [starRating, setStarRating] = React.useState<number | null>(model?.socialStatus?.averageStarRating ?? 0);
  const [writeAccess, setWriteAccess] = React.useState<boolean | null>(model.permissions?.includes('Write') ? true: false ?? 0);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [review, setReview] = useState(defaultReview);
  const navigate = useNavigate();
  const [errorMessages, setErrorMessages] = useState();
  const [imageData, setImageData] = useState('');
  const [logoUrl, setLogoUrl] = useState(favicon);
  const [imageLoading, setImageLoading] = useState(false);
  const [showClaimModel, setShowClaimModel] = useState(false);
  const [officialLinks, setOfficialLinks] = useState<ExternalLinkInfo[]>([]);
  const [supportingLinksWithVideoOrImages, setSupportingLinksWithVideoOrImages] = useState<ExternalLinkInfo[]>([]);
  const { searchForm } = useSelector(SearchSelector);
  const dispatch = useAppDispatch();
  const [expandedAccordions, setExpandedAccordions] = useState(['official-link', 'details-section']);
  const formComponentRef = useRef();
  const { userInfo } = useSelector(userInfoSelector);
  


  // ▶️ Functions/Methods .....



  const accordionClicked = (panel) => {
    // console.log('panel', panel, expandedAccordions);
    if (panel === 'inference' && !expandedAccordions.includes(panel)) {
      setExpandedAccordions(['inference']);
    } else if (panel === 'inference' && expandedAccordions.includes(panel)) {
      setExpandedAccordions(expandedAccordions.filter((p) => p !== panel));
    } else if (panel !== 'inference' && expandedAccordions.includes(panel)) {
      setExpandedAccordions(expandedAccordions.filter((p) => p !== panel));
    } else setExpandedAccordions([...expandedAccordions, panel]);
  };

  const openClaimModal = () => {
    if (!isAuthenticated) {
      loginWithRedirect();
    }
    setShowClaimModel(true);
  };

  const getScreenshot = async (url) => {
    try {
      setImageLoading(true);
      if (url && url !== '') {
        const response = await ApiHelper.GetScreenshotByUrl(url);
        if (response.status === 200 && response.data) {
          setImageLoading(false);
          setImageData(response.data);
        } else {
          setImageLoading(false);
        }
      } else {
        setImageLoading(false);
      }
    } catch (e) {
      setImageLoading(false);
      console.log('error', e);
    }
  };

  const handleTooltip = () => {
    setOpenTooltip(!openTooltip);
  };

  const openExternalLink = () => {
    if (model.externalRedirectUrl) window.open(model.externalRedirectUrl);
  };

  async function isImgUrl(url) {
    const img = new Image();
    img.src = url;

    return new Promise((resolve) => {
      img.onerror = () => resolve(false);
      img.onload = () => resolve(true);
    });
  }

  const setlogoUrl = async () => {
    let imageUrl = favicon;
    if (model.supportingLinks.length > 0) {
      const links = model.supportingLinks.filter(
        (l) => l.linkType === ExternalLinkType.Logo || l.linkType === ExternalLinkType.FavIcon
      );
      if (links.length === 1) {
        imageUrl = (await isImgUrl(links[0].url)) ? links[0].url : favicon;
      } else {
        const url = links.find((l) => l.linkType === ExternalLinkType.Logo)?.url;
        imageUrl = (await isImgUrl(url)) ? url : favicon;
      }
    }
    // console.log('imageUrl', imageUrl);
    setLogoUrl(imageUrl);
  };

  const goToSearch = async (param) => {
    const form = cloneDeep(searchForm);
    form.category = param.category;
    dispatch(UpdateSearchForm(form));
    const url = `${window.location.origin}/search?type=ai&category=${param.category}&sort=Relevance`;
    window.open(url, '_blank');
  };

  const onHeaderAction = (text) => {
    if (model && model.modelType === 'HostedModel') {
      accordionClicked('inference');
    } else {
      openExternalLink();
    }
  };

  //#region <Review code started>
  const getReviews = async () => {
    setIsLoading(true);
    try {
      const response = await ApiHelper.GetReviews(model?.modelId);
      if (response && response.data) {
        setReviews(response.data);
        setIsLoading(false);
      }
    } catch (e) {
      setIsLoading(false);
      console.error('Unable to get reviews', e);
    }
  };

  const saveReview = async (data: ModelReview) => {
    try {
      const response = await ApiHelper.UpsertReview(data, model.modelId);
      if (response && response.data) {
        setShowReviewModal(false);
        alert('Thank you for your valuable feedback.');
        await getReviews();
      }
    } catch (e) {
      alert('Unable to post review', 'error');
      setShowReviewModal(false);
    }
  };

  const openReviewModal = async (ratingValue) => {
    // if (!ratingValue) {
    //   return;
    // }
    if (!isAuthenticated) {
      return loginWithRedirect();
    }
    setIsLoading(true);
    setStarRating(ratingValue);
    try {
      const response = await ApiHelper.GetReviewByUserId(model?.modelId);
      // console.log('review', response);
      if (response && response.data) {
        const review = response.data;
        setReview(review);
        setShowReviewModal(true);
      }
      setIsLoading(false);
    } catch (error) {
      // console.log('review', error);
      if (error?.response?.status === 404) {
        const review = defaultReview;
        setReview(review);
        setShowReviewModal(true);
      }
      setIsLoading(false);
    }
  };

  const getGridStyle = (column: string) => {
    const smallScreen = 900;
    let style = {};
    if (widthSize && widthSize > smallScreen) {
      if (column === 'left') {
        style = {
          paddingLeft: '10px',
        };
      } else if (column === 'right') {
        style = {
          paddingLeft: '10px',
          borderStyle: 'dotted',
          borderColor: 'rgba(0, 0, 0, 0.12)',
          borderWidth: '0 0 0 2px',
        };
      }
    }
    return style;
  };


  // ▶️ Hooks .....



  useEffect(() => {
    setIsHostedModel(model && model.modelType === 'HostedModel');
    getReviews();
   
    setlogoUrl();
    if (model.supportingLinks.length > 0) {
      const links = model.supportingLinks.filter(
        (l) =>
          l.linkType !== ExternalLinkType.Logo &&
          l.linkType !== ExternalLinkType.FavIcon &&
          l.linkType !== ExternalLinkType.SupportingImage &&
          l.linkType !== ExternalLinkType.SupportingVideo
      );
      // console.log('links', links);
      setOfficialLinks(links);
      const linksWithVideoOrImages = model.supportingLinks.filter(
        (l) => l.linkType === ExternalLinkType.SupportingImage || l.linkType === ExternalLinkType.SupportingVideo
      );
      const supportingImage = model.supportingLinks.find(
        (l) => l.linkType === ExternalLinkType.SupportingImage || l.linkType === ExternalLinkType.SupportingVideo
      );
      if (supportingImage) {
        getScreenshot(supportingImage.url);
      }

      setSupportingLinksWithVideoOrImages(linksWithVideoOrImages);
    }
  }, [model]);

 

  return (
    <>
      <div className="model-viewer">
        <HeaderSection
          model={model}
          logoUrl={logoUrl}
          headerAction={onHeaderAction}
          openReviewModal={openReviewModal}
          openExternalLink={openExternalLink}
          goToSearch={goToSearch}
        />

        <Grid container>
          {model.modelDescription && model.modelDescription !== '' && (
            <Grid item md={12}>
              <p className="model-desc"> {model.modelDescription ?? ''}</p>
            </Grid>
          )}
          {/* <ModelViewerLine /> */}
          {(officialLinks.length > 0 || supportingLinksWithVideoOrImages.length > 0) && (
            <Grid item lg={12} xs={12} md={12} sm={12}>
              <Accordion
                onChange={() => accordionClicked('official-link')}
                expanded={expandedAccordions.includes('official-link')}
              >
                <AccordionSummary
                  className={expandedAccordions.includes('official-link') ? 'accordion-summary-custom' : ''} // {expanded === 'details-section' ? 'accordion-summary-custom' : ''}
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="official-link"
                  id="official-link"
                >
                  <div className="accordion-summary-title">Official Links</div>
                </AccordionSummary>
                <AccordionDetails className="details-container">
                  {officialLinks.length > 0 && <OfficialLink model={model} officialLinks={officialLinks} openExternalLink={openExternalLink} />}
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}
          {supportingLinksWithVideoOrImages.length > 0 && expandedAccordions.includes('official-link') && (
            <VideosAndImagesSection
              supportingLinksWithVideoOrImages={supportingLinksWithVideoOrImages}
              externalRedirectUrl={model?.externalRedirectUrl}
            />
          )}
          <Grid item xs={12}>
            {/* ## Details ## */}
            <Accordion
              onChange={() => accordionClicked('details-section')}
              expanded={expandedAccordions.includes('details-section')}
            >
              <AccordionSummary
                className={expandedAccordions.includes('details-section') ? 'accordion-summary-custom' : ''} // {expanded === 'details-section' ? 'accordion-summary-custom' : ''}
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1bh-content"
                id="panel1bh-header"
              >
                <div className="accordion-summary-title">Details</div>
                {/* <Typography sx={{ color: 'text.secondary' }}>I am an accordion</Typography> */}
              </AccordionSummary>
              <AccordionDetails className="details-container">
                <DetailsSection model={model} openClaimModal={openClaimModal} editEnabled={model?.permissions?.includes('Write')} getGridStyle={getGridStyle} />
              </AccordionDetails>
            </Accordion>

            {/* ## Inference View ## */}
            {isHostedModel && (
              <Accordion onChange={() => accordionClicked('inference')} expanded={expandedAccordions.includes('inference')}>
                <AccordionSummary
                  className={expandedAccordions.includes('inference') ? 'accordion-summary-custom' : ''}
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="inference-content"
                  id="inference-header"
                >
                  <div className="accordion-summary-title">Inference</div>
                  {/* <h6 style={{ marginBottom: '5px', fontWeight: '600' }}>Inference</h6> */}
                  {/* <Typography sx={{ color: 'text.secondary' }}>I am an accordion</Typography> */}
                </AccordionSummary>
                <AccordionDetails>{model && <Inference model={model} />}</AccordionDetails>
              </Accordion>
            )}
            
            {/* ## Reviews ## */}
            <Accordion
              className="accordion-summary-custom"
              onChange={() => accordionClicked('reviews')}
              expanded={expandedAccordions.includes('reviews')}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="reviews-content"
                id="reviews-header"
                className={expandedAccordions.includes('reviews') ? 'accordion-summary-custom' : ''}
              >
                <div className="accordion-summary-title">Reviews</div>
              </AccordionSummary>
              <AccordionDetails>
                {reviews.map((review) => (
                  <ItemContent reviewItem={review} type="review" key={review.id} />
                ))}
              </AccordionDetails>
            </Accordion>
            {showReviewModal && (
              <ReviewModal
                onSave={saveReview}
                open={showReviewModal}
                review={review}
                handleClose={() => setShowReviewModal(false)}
              />
            )}
            {showClaimModel && userInfo && (
              <ClaimModel
                userId={userInfo.userId}
                modelId={model.modelId}
                open={showClaimModel}
                close={() => setShowClaimModel(false)}
              />
            )}
          </Grid>
        </Grid>

      </div>
    </>
  );
};

export default ModelViewer;
